// The start screen (Matt, 2026-10-14: "generate proper logos and a start screen"). The backdrop is one
// ByteDance still (art/brand/title-bg.webp, tools/art/brand.py); everything that moves is drawn here, because a
// video model redraws pixel art as smooth 3D: drifting fog, sodium lamps (one of them dying), the Tavern's
// windows, the aurora, light snow, and a few of the turned shambling along the street.
const BG_W = 1920, BG_H = 1080;
// where things are in the backdrop, as fractions of it
const LAMPS: [number, number, number][] = [   // x, y of the lamp head, radius
  [0.1875, 0.618, 0.05], [0.405, 0.65, 0.035], [0.54, 0.674, 0.03], [0.662, 0.699, 0.02], [0.7125, 0.711, 0.016],
  [0.857, 0.583, 0.05], [0.866, 0.669, 0.03], [0.875, 0.70, 0.02],
];
const DYING = 2;                                       // the lamp that cannot make up its mind
const TAVERN = [0.435, 0.705, 0.506, 0.761];           // its lit windows
const FOCUS_X = 0.47;                                  // portrait phones crop round the Tavern

interface Walker { x: number; dir: number; speed: number; feet: number; scale: number; t: number; sheet: number }

export class TitleFx {
  cv: HTMLCanvasElement; g: CanvasRenderingContext2D;
  bg = new Image(); fog: HTMLCanvasElement; sheets: { frames: HTMLCanvasElement[] }[] = [];
  flakes: [number, number, number][] = []; walkers: Walker[] = [];
  raf = 0; last = 0; t = 0; on = false;

  constructor(cv: HTMLCanvasElement) {
    this.cv = cv; this.g = cv.getContext('2d')!;
    this.bg.src = 'art/brand/title-bg.webp';
    this.fog = this.makeFog();
    for (let i = 0; i < 140; i++) this.flakes.push([Math.random(), Math.random(), 0.4 + Math.random() * 0.6]);
    for (const k of ['turned_commuter', 'turned_pylon', 'turned_beerleague']) this.loadSheet(k);
    addEventListener('resize', () => this.fit());
    this.fit();
  }

  /** soft fog: blobs on a small canvas that tiles horizontally */
  makeFog(): HTMLCanvasElement {
    const c = document.createElement('canvas'); c.width = 512; c.height = 128;
    const g = c.getContext('2d')!;
    for (let i = 0; i < 46; i++) {
      const x = Math.random() * 512, y = 40 + Math.random() * 80, r = 20 + Math.random() * 46;
      for (const ox of [-512, 0, 512]) {
        const gr = g.createRadialGradient(x + ox, y, 0, x + ox, y, r);
        gr.addColorStop(0, 'rgba(156,196,198,0.22)'); gr.addColorStop(1, 'rgba(156,196,198,0)');
        g.fillStyle = gr; g.fillRect(x + ox - r, y - r, r * 2, r * 2);
      }
    }
    return c;
  }

  /** a turned walk cycle, pre-darkened to a night silhouette with a little teal from the fog */
  async loadSheet(key: string) {
    try {
      const meta = await (await fetch(`sprites/iso_${key}.json`)).json();
      const img = new Image(); img.src = `sprites/iso_${key}.png`; await img.decode();
      const frames: HTMLCanvasElement[] = [];
      for (const i of meta.anims.walk_dl as number[]) {
        const c = document.createElement('canvas'); c.width = meta.frameW; c.height = meta.frameH;
        const g = c.getContext('2d')!;
        g.drawImage(img, (i % meta.cols) * meta.frameW, Math.floor(i / meta.cols) * meta.frameH, meta.frameW, meta.frameH, 0, 0, meta.frameW, meta.frameH);
        const d = g.getImageData(0, 0, c.width, c.height);
        for (let p = 0; p < d.data.length; p += 4) {
          d.data[p] = d.data[p] * 0.28 + 10; d.data[p + 1] = d.data[p + 1] * 0.3 + 16; d.data[p + 2] = d.data[p + 2] * 0.34 + 24;
        }
        g.putImageData(d, 0, 0);
        frames.push(c);
      }
      if (frames.length) this.sheets.push({ frames });
    } catch { /* no walkers is fine */ }
  }

  fit() {
    const dpr = Math.min(2, devicePixelRatio || 1);
    this.cv.width = Math.round(innerWidth * dpr); this.cv.height = Math.round(innerHeight * dpr);
  }

  /** cover-fit the backdrop: returns scale and offset that map backdrop pixels to canvas pixels */
  view() {
    const W = this.cv.width, H = this.cv.height;
    const s = Math.max(W / BG_W, H / BG_H);
    const ox = Math.min(0, Math.max(W - BG_W * s, W / 2 - FOCUS_X * BG_W * s));
    const oy = H - BG_H * s;                          // keep the street; any crop comes off the sky
    return { s, ox, oy, W, H };
  }

  start() {
    if (this.on) return;
    this.on = true; this.last = performance.now();
    const loop = (now: number) => {
      if (!this.on) return;
      const dt = Math.min(0.1, (now - this.last) / 1000); this.last = now; this.t += dt;
      this.draw(dt);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }
  stop() { this.on = false; cancelAnimationFrame(this.raf); }

  spawn() {
    if (!this.sheets.length || this.walkers.length >= 3 || Math.random() > 0.01) return;
    const far = Math.random() < 0.5, dir = Math.random() < 0.5 ? -1 : 1;
    this.walkers.push({ x: dir > 0 ? -0.05 : 1.05, dir, speed: (far ? 0.006 : 0.011) * (0.8 + Math.random() * 0.4),
      feet: far ? 0.84 : 0.93, scale: far ? 0.09 : 0.14, t: Math.random() * 4, sheet: Math.floor(Math.random() * this.sheets.length) });
  }

  draw(dt: number) {
    const { g } = this; const { s, ox, oy, W, H } = this.view();
    const X = (fx: number) => ox + fx * BG_W * s, Y = (fy: number) => oy + fy * BG_H * s;
    g.globalCompositeOperation = 'source-over'; g.globalAlpha = 1;
    g.fillStyle = '#0f0e22'; g.fillRect(0, 0, W, H);
    if (this.bg.complete && this.bg.naturalWidth) { g.imageSmoothingEnabled = true; g.drawImage(this.bg, ox, oy, BG_W * s, BG_H * s); }

    // aurora: a slow green-to-violet breathing band behind the rooftops
    g.globalCompositeOperation = 'lighter';
    const au = 0.10 + 0.06 * Math.sin(this.t * 0.35) + 0.03 * Math.sin(this.t * 1.3);
    for (let i = 0; i < 3; i++) {
      const cx = X(0.5 + 0.12 * i + 0.03 * Math.sin(this.t * 0.2 + i)), cy = Y(0.42 + 0.03 * i), r = 0.22 * BG_W * s;
      const gr = g.createRadialGradient(cx, cy, 0, cx, cy, r);
      gr.addColorStop(0, i === 1 ? `rgba(120,80,200,${au * 0.6})` : `rgba(60,220,150,${au})`); gr.addColorStop(1, 'rgba(0,0,0,0)');
      g.save(); g.translate(cx, cy); g.scale(1, 0.28); g.translate(-cx, -cy); g.fillStyle = gr; g.fillRect(cx - r, cy - r, r * 2, r * 2); g.restore();
    }

    // sodium lamps; one of them is dying
    LAMPS.forEach(([lx, ly, lr], i) => {
      let a = 0.42 + 0.04 * Math.sin(this.t * 3 + i * 1.7);
      if (i === DYING) { const p = (this.t * 1.7) % 6; a *= p < 0.15 || (p > 0.35 && p < 0.45) || (p > 2.1 && p < 2.6) ? 0.1 : 1; }
      const cx = X(lx), cy = Y(ly), r = lr * BG_W * s;
      const gr = g.createRadialGradient(cx, cy, 0, cx, cy, r);
      gr.addColorStop(0, `rgba(255,170,80,${a})`); gr.addColorStop(0.35, `rgba(255,120,40,${a * 0.35})`); gr.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = gr; g.fillRect(cx - r, cy - r, r * 2, r * 2);
      if (i === DYING && a < 0.1) { g.globalCompositeOperation = 'source-over'; g.fillStyle = 'rgba(15,14,34,0.55)'; g.fillRect(cx - r * 0.25, cy - r * 0.25, r * 0.5, r * 0.5); g.globalCompositeOperation = 'lighter'; }
    });
    // the Tavern is open: a warm, slightly uneven glow, someone moving past the window now and then
    const tw = 0.16 + 0.04 * Math.sin(this.t * 7) * Math.sin(this.t * 2.3);
    const [tx0, ty0, tx1, ty1] = TAVERN;
    g.fillStyle = `rgba(255,150,60,${tw})`; g.fillRect(X(tx0), Y(ty0), X(tx1) - X(tx0), Y(ty1) - Y(ty0));
    g.globalCompositeOperation = 'source-over';
    const pass = (this.t * 0.08) % 1.6;
    if (pass < 1) { g.fillStyle = 'rgba(30,14,10,0.35)'; g.fillRect(X(tx0 + (tx1 - tx0) * pass) - 0.004 * BG_W * s, Y(ty0 + 0.01), 0.008 * BG_W * s, Y(ty1) - Y(ty0 + 0.01)); }

    // the turned, along the street
    this.spawn();
    g.imageSmoothingEnabled = false;
    this.walkers = this.walkers.filter(w => {
      w.x += w.dir * w.speed * dt; w.t += dt;
      const fr = this.sheets[w.sheet].frames, f = fr[Math.floor(w.t * 3.2) % fr.length];
      const h = w.scale * BG_H * s, ww = h * f.width / f.height, px = X(w.x), py = Y(w.feet);
      g.save(); g.translate(px, py); if (w.dir > 0) g.scale(-1, 1);
      g.drawImage(f, -ww / 2, -h * 0.94, ww, h); g.restore();
      return w.x > -0.1 && w.x < 1.1;
    });
    g.imageSmoothingEnabled = true;

    // fog in two layers, the near one faster
    for (const [spd, fy, a, k] of [[14, 0.8, 0.9, 1.6], [26, 0.9, 1, 2.4]] as const) {
      const fh = this.fog.height * k * s * 1.5, fw = this.fog.width * k * s * 1.5;
      const off = -((this.t * spd * s) % fw);
      g.globalAlpha = a;
      for (let x = off; x < W; x += fw) g.drawImage(this.fog, x, Y(fy) - fh / 2, fw, fh);
    }
    g.globalAlpha = 1;

    // snow: square flakes, a little wind
    const px = Math.max(1, Math.round(2 * s * 1.4));
    g.fillStyle = 'rgba(242,246,255,0.75)';
    for (const f of this.flakes) {
      f[1] += dt * 0.05 * f[2]; f[0] += dt * (0.012 + 0.01 * Math.sin(this.t + f[1] * 9));
      if (f[1] > 1) { f[1] = 0; f[0] = Math.random(); } if (f[0] > 1) f[0] -= 1;
      g.fillRect(Math.round(f[0] * W), Math.round(f[1] * H), px * (f[2] > 0.8 ? 2 : 1), px * (f[2] > 0.8 ? 2 : 1));
    }
  }
}
